import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, Zap, Mail, ShieldCheck, Clock, ArrowLeft, Heart, Gift, Timer, HelpCircle, AlertTriangle, Play, Ticket } from "lucide-react";
import api, { getErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { NeonButton, NeonCard, NeonPrice, Spinner, Pill } from "../components/Neon";
import { gbp, timeParts, fmtDate } from "../lib/format";
import { checkSpend, recordSpend } from "../lib/responsible";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { useDocumentSeo, getSeo, useJsonLd } from "../lib/seo";
import { useSiteConfig } from "../context/SiteConfigContext";

export default function CompetitionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthed, isVerified, isAdmin, user, refreshUser } = useAuth();
  const { maintenanceMode, charityRoundupEnabled, charityName } = useSiteConfig();
  const [comp, setComp] = useState(null);
  // During maintenance, ALL competitions are locked for customers (including launch specials).
  // Admins retain access so they can prepare/test draws.
  const salesPaused = maintenanceMode && !isAdmin;
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [answer, setAnswer] = useState(null);
  const [discount, setDiscount] = useState("");
  const [useWallet, setUseWallet] = useState(false);
  const [charityIncrement, setCharityIncrement] = useState(null); // null | 0.10 | 1.00 (round UP to nearest)
  const [buying, setBuying] = useState(false);
  const [result, setResult] = useState(null);
  const [offer, setOffer] = useState(null); // win-back offer { discount_pct, expires_at }
  const [skillError, setSkillError] = useState(null); // UK-law wrong-answer modal message
  const [showWhy, setShowWhy] = useState(false);
  const [goldenTickets, setGoldenTickets] = useState([]); // available golden tickets (forward-compatible)
  const [goldenBusy, setGoldenBusy] = useState(false);
  const [goldenReload, setGoldenReload] = useState(0);
  const [friendDiscount, setFriendDiscount] = useState(null); // {amount, min} when the referred-friend £ discount is available
  const neuro = !!user?.neuro_features;
  const interactedRef = useRef(false);
  const purchasedRef = useRef(false);

  // A competition is "expired" once it's drawn/ended/archived (but launch specials with a
  // far-future end date are NOT expired). Expired pages send visitors to the Winners board.
  const isExpiredComp = (c) => {
    if (!c) return false;
    const s = String(c.status || "").toLowerCase();
    if (["ended", "archived", "complete", "completed", "drawn", "closed", "finished"].includes(s)) return true;
    if (c.winner) return true;
    const tp = timeParts(c.ends_at);
    if (tp.done && !(c.launch_draw || c.is_launch_competition)) return true;
    return false;
  };

  const load = () => api.get(`/competitions/${id}`)
    .then(({ data }) => {
      if (isExpiredComp(data)) { navigate("/winners", { replace: true }); return; }
      setComp(data);
    })
    .catch((e) => {
      // Archived/removed competitions return 404 — treat as expired and send to Winners.
      if (e?.response?.status === 404) { navigate("/winners", { replace: true }); return; }
      toast.error("Competition not found");
    })
    .finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Check for an existing abandoned-cart win-back offer for this competition.
  useEffect(() => {
    if (!isAuthed) { setOffer(null); return; }
    let active = true;
    api.get(`/cart/offer/${id}`).then(({ data }) => {
      if (active && data && data.active) setOffer(data);
    }).catch(() => {});
    return () => { active = false; };
  }, [id, isAuthed]);

  // Load the user's Golden Tickets + referral friend-discount status. Forward-compatible: uses
  // /me/golden-tickets when it exists, else /me/referrals.golden_tickets. Silent until backend grants.
  useEffect(() => {
    if (!isAuthed) { setGoldenTickets([]); setFriendDiscount(null); return; }
    let active = true;
    (async () => {
      let ref = null;
      try { const { data } = await api.get("/me/referrals"); ref = data; } catch { /* ignore */ }
      let list = ref?.golden_tickets || [];
      try {
        const { data } = await api.get("/me/golden-tickets");
        list = Array.isArray(data) ? data : (data.golden_tickets || data.tickets || list);
      } catch { /* keep fallback */ }
      if (!active) return;
      setGoldenTickets((list || []).filter((g) => String(g.status || "available") === "available"));
      setFriendDiscount(ref && ref.friend_discount_available
        ? { amount: ref.friend_discount_amount ?? 0, min: ref.friend_discount_min ?? 0 } : null);
    })();
    return () => { active = false; };
  }, [id, isAuthed, goldenReload]);

  // Keep a ref of the latest quantity for the unmount handler below.
  const qtyRef = useRef(1);
  useEffect(() => { qtyRef.current = qty; }, [qty]);

  // If a logged-in user engaged with checkout but left without buying, record an abandoned cart.
  useEffect(() => {
    return () => {
      if (isAuthed && interactedRef.current && !purchasedRef.current) {
        api.post("/cart/abandon", { competition_id: id, quantity: qtyRef.current || 1 }).catch(() => {});
      }
    };
    // eslint-disable-next-line
  }, [id, isAuthed]);

  const seo = comp ? getSeo(comp) : {};
  useDocumentSeo({
    title: comp ? (seo.title || `${comp.title} | Richi£ Rich Competitions`) : undefined,
    description: comp ? (seo.desc || (comp.description || "").replace(/\s+/g, " ").trim().slice(0, 155)) : undefined,
    keywords: seo.keywords || undefined,
    canonical: typeof window !== "undefined" ? `${window.location.origin}/competitions/${id}` : undefined,
    image: comp?.hero_image || undefined,
  });

  // Pull the backend's schema.org structured data (Product/Offer + Breadcrumb + FAQ) and inject it.
  const [jsonLd, setJsonLd] = useState(null);
  useEffect(() => {
    let active = true;
    api.get(`/competitions/${id}/jsonld`).then(({ data }) => { if (active) setJsonLd(data); }).catch(() => {});
    return () => { active = false; };
  }, [id]);
  useJsonLd(jsonLd);

  if (loading) return <Spinner className="min-h-[60vh]" />;
  if (!comp) return <div className="mx-auto max-w-3xl px-4 py-20 text-center text-[var(--text-muted)]">Competition not found.</div>;

  const t = timeParts(comp.ends_at);
  const sold = comp.tickets_sold || 0;
  const total = comp.total_tickets || 0;
  const pct = total ? Math.min(100, Math.round((sold / total) * 100)) : 0;
  const maxPer = comp.max_tickets_per_user && comp.max_tickets_per_user > 0 ? comp.max_tickets_per_user : (comp.effective_ticket_limit || 100);
  const lineTotal = (comp.ticket_price * qty);
  // Charity round-up: lift the basket UP to the nearest 10p or nearest £1.
  // If the total already sits exactly on the increment, round up to the next one so the donation is never £0.
  const roundupAmount = (() => {
    if (!charityIncrement) return 0;
    // Smallest multiple of the increment strictly above the current total (epsilon bumps exact multiples to the next step).
    const steps = Math.ceil((lineTotal + 0.0001) / charityIncrement);
    const up = steps * charityIncrement;
    return Math.round((up - lineTotal) * 100) / 100;
  })();
  const previewRoundup = (inc) => {
    const steps = Math.ceil((lineTotal + 0.0001) / inc);
    return Math.round((steps * inc - lineTotal) * 100) / 100;
  };
  // Send a server-side mode token so the backend rounds the DISCOUNTED total up and
  // donates the exact difference (matches the mobile app). Display amount is an estimate.
  const charityRoundupValue = charityIncrement === 0.1 ? "10p" : charityIncrement === 1 ? "1" : null;
  const grandTotal = Math.round((lineTotal + roundupAmount) * 100) / 100;

  const setQtySafe = (v) => { interactedRef.current = true; setQty(Math.max(1, Math.min(maxPer, v))); };

  const buy = async () => {
    if (salesPaused) { toast.error("Ticket sales are paused for maintenance. Please try again soon."); return; }
    if (!isAuthed) { toast.error("Please log in to enter."); navigate("/login", { state: { from: `/competitions/${id}` } }); return; }
    if (!isVerified) { toast.error("Verify your email & phone first."); navigate("/profile"); return; }
    if (answer === null) { toast.error("Please answer the skill question."); return; }
    const block = checkSpend(lineTotal);
    if (block) { toast.error(block); return; }
    setBuying(true);
    try {
      const { data } = await api.post(`/competitions/${id}/purchase`, {
        quantity: qty, answer_index: answer, use_wallet: useWallet, discount_code: discount || null,
        charity_roundup: charityRoundupValue,
      });
      purchasedRef.current = true;
      recordSpend(useWallet ? 0 : lineTotal);
      setResult(data);
      setOffer(null);
      toast.success(`You're in! ${data.tickets?.length || qty} ticket(s) secured.`);
      if (data.charity_donation > 0) {
        toast.success(`Thank you! ${gbp(data.charity_donation)} donated to ${data.charity_name || "charity"}.`);
      }
      refreshUser();
      load();
    } catch (e) {
      // UK law: a wrong skill answer returns HTTP 400 with NO charge and NO tickets.
      // Show a prominent modal (not just a toast), clear the answer, and let them retry.
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "";
      if (e?.response?.status === 400 && /incorrect answer|skill question/i.test(msg)) {
        setAnswer(null);
        setSkillError(msg || "Incorrect answer to the skill question — your entry was not accepted and no payment was taken. Please try again.");
      } else {
        toast.error(getErr(e, "Purchase failed"));
      }
    } finally {
      setBuying(false);
    }
  };

  // A Golden Ticket grants a free entry to any competition with a ticket price up to its cap (£1).
  const eligibleGolden = goldenTickets.find((g) => Number(g.max_ticket_value ?? 1) >= Number(comp.ticket_price || 0));
  const showGolden = !!eligibleGolden && !result && !t.done && !salesPaused;

  const useGolden = async () => {
    if (!isAuthed) { navigate("/login", { state: { from: `/competitions/${id}` } }); return; }
    if (!isVerified) { toast.error("Verify your email & phone first."); navigate("/profile"); return; }
    if (answer === null) { toast.error("Please answer the skill question first — it's legally required, even for a free entry."); return; }
    setGoldenBusy(true);
    try {
      const { data } = await api.post(`/golden-tickets/${eligibleGolden.id}/redeem`, { competition_id: id, answer_index: answer });
      purchasedRef.current = true;
      setResult(data && (data.tickets || data.ticket_numbers) ? { ...data, tickets: data.tickets || data.ticket_numbers } : { tickets: [] });
      toast.success("Golden Ticket redeemed — free entry secured!");
      refreshUser(); load(); setGoldenReload((n) => n + 1);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "";
      if (e?.response?.status === 400 && /incorrect answer|skill question/i.test(msg)) {
        setAnswer(null);
        setSkillError(msg || "Incorrect answer to the skill question — your entry was not accepted. Please try again.");
      } else {
        toast.error(getErr(e, "Could not redeem your Golden Ticket"));
      }
    } finally { setGoldenBusy(false); }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[var(--text-muted)] hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</button>
      <div className="mt-4 grid lg:grid-cols-2 gap-8">
        {/* Media */}
        <div>
          <div className="neon-flow"><div className="neon-inner p-0 overflow-hidden relative">
            {comp.hero_video
              ? <video src={comp.hero_video} poster={comp.hero_image || undefined} autoPlay muted loop playsInline preload="metadata" className="w-full aspect-[16/11] object-cover" data-testid="detail-hero-video" />
              : <img src={comp.hero_image} alt={comp.title} className="w-full aspect-[16/11] object-cover" />}
            {comp.hero_video && (
              <span data-testid="detail-animated-pill" className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-[var(--neon-gold)] px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-black shadow-[0_0_16px_rgba(255,184,0,.55)]">
                <Play className="h-3.5 w-3.5" /> Animated
              </span>
            )}
          </div></div>
          <div className="mt-4 flex flex-wrap gap-2">
            {comp.featured && <Pill color="pink">Featured</Pill>}
            {comp.instant_wins_enabled && <Pill color="cyan"><Zap className="h-3 w-3" /> {comp.instant_wins_remaining} Instant Wins left</Pill>}
            {comp.subscriber_eligible && <Pill color="green">Members eligible</Pill>}
            {comp.free_postal_enabled && <Pill color="grey"><Mail className="h-3 w-3" /> Free postal route</Pill>}
          </div>
          <NeonCard variant="soft" className="mt-4" innerClassName="p-5">
            <h3 className="font-display uppercase text-xl text-white">Prize Details</h3>
            <p className="mt-2 text-[var(--text-muted)] text-sm">{comp.description}</p>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div><p className="text-[var(--text-muted)]">Prize</p><p className="text-white">{comp.prize_title}</p></div>
              <div><p className="text-[var(--text-muted)]">Prize value</p><p className="glow-pink font-display text-lg">{gbp(comp.prize_value)}</p></div>
              <div><p className="text-[var(--text-muted)]">Cash alternative</p><p className="glow-green font-display text-lg">{gbp(comp.cash_alternative)}</p></div>
              <div><p className="text-[var(--text-muted)]">Draw closes</p><p className="text-white">{fmtDate(comp.ends_at)}</p></div>
            </div>
          </NeonCard>
        </div>

        {/* Purchase panel */}
        <div className="lg:sticky lg:top-20 lg:self-start">
          <NeonCard variant="flow" innerClassName="p-6">
            <h1 className="font-display uppercase text-4xl text-white glow-white leading-tight">{comp.title}</h1>
            <div className="mt-2 flex items-center gap-3">
              <NeonPrice value={gbp(comp.ticket_price)} color="green" className="text-3xl" /> <span className="text-[var(--text-muted)]">per ticket</span>
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-sm text-[var(--text-muted)]"><span>{sold} / {total} tickets sold</span><span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {t.done ? "Closed" : `${t.d}d ${t.h}h ${t.m}m left`}</span></div>
              <div className="mt-2 h-2.5 w-full rounded-full bg-white/10 overflow-hidden"><div className="h-full rrc-progress" style={{ width: `${pct}%` }} /></div>
            </div>

            {offer && !result && (
              <div data-testid="winback-offer-banner" className="mt-4 flex items-start gap-3 rounded-xl border border-[rgba(255,184,0,.5)] bg-[rgba(255,184,0,.08)] px-4 py-3">
                <Gift className="h-5 w-5 shrink-0 text-[var(--neon-gold)]" />
                <div className="text-sm">
                  <p className="font-display uppercase tracking-wide text-[var(--neon-gold)]">Welcome back — {Math.round(offer.discount_pct)}% off waiting</p>
                  <p className="mt-0.5 text-xs text-[var(--text-muted)]">You left this draw earlier. Your {Math.round(offer.discount_pct)}% win-back discount is applied automatically at checkout{offer.expires_at ? <> — <Timer className="inline h-3 w-3" /> ends {fmtDate(offer.expires_at)}</> : null}.</p>
                </div>
              </div>
            )}

            {result ? (
              <div className="mt-6">
                <NeonCard variant="green" innerClassName="p-5">
                  <p className="font-display uppercase text-2xl glow-green">You're in!</p>
                  <p className="mt-1 text-sm text-[var(--text-muted)]">Ticket numbers: <span className="text-white">{result.tickets?.join(", ")}</span></p>
                  {result.instant_wins?.length > 0 && <p className="mt-2 text-[var(--neon-cyan)]">⚡ Instant win triggered!</p>}
                  <div className="mt-4 flex gap-3">
                    <NeonButton as={Link} to="/my-tickets" variant="outlineCyan">View My Tickets</NeonButton>
                    <NeonButton variant="ghost" onClick={() => setResult(null)}>Buy more</NeonButton>
                  </div>
                </NeonCard>
              </div>
            ) : (
              <>
                {/* Skill question */}
                <div className="mt-6" data-testid="competition-skill-question">
                  <div className="flex items-center gap-2">
                    <p className="font-display uppercase tracking-wide text-[var(--neon-cyan)]">Skill Question</p>
                    <button type="button" onClick={() => setShowWhy((v) => !v)} data-testid="skill-why-toggle" aria-label="Why a skill question?" className="text-[var(--text-muted)] hover:text-[var(--neon-cyan)]"><HelpCircle className="h-4 w-4" /></button>
                  </div>
                  {showWhy && (
                    <p data-testid="skill-why-text" className="mt-1 rounded-lg border border-[rgba(0,229,255,.25)] bg-[rgba(0,229,255,.05)] px-3 py-2 text-[11px] text-[var(--text-muted)]">
                      UK law requires a genuine skill question to enter. A wrong answer means no entry and no payment is taken — but you can simply try again.
                    </p>
                  )}
                  <p className="mt-1 text-white">{comp.skill_question?.question}</p>
                  <RadioGroup className="mt-3 grid grid-cols-2 gap-2" value={answer === null ? "" : String(answer)} onValueChange={(v) => { interactedRef.current = true; setAnswer(Number(v)); }}>
                    {(comp.skill_question?.options || []).map((opt, i) => (
                      <label key={i} data-testid={`skill-option-${i}`} className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 cursor-pointer text-sm ${answer === i ? "border-[var(--neon-pink)] text-white bg-[rgba(255,45,149,.08)]" : "border-white/10 text-[var(--text-muted)] hover:border-white/30"}`}>
                        <RadioGroupItem value={String(i)} className="border-white/30 text-[var(--neon-pink)]" />
                        {opt}
                      </label>
                    ))}
                  </RadioGroup>
                </div>

                {/* Quantity */}
                <div className="mt-6">
                  <p className="font-display uppercase tracking-wide text-[var(--neon-cyan)]">Tickets</p>
                  {neuro && (
                    <div data-testid="quick-buy-presets" className="mt-2 flex flex-wrap gap-2">
                      {[1, 5, 10, 25].map((n) => (
                        <button key={n} data-testid={`quick-buy-${n}`} onClick={() => setQtySafe(n)}
                          className={`rounded-xl px-4 py-2 text-sm font-display uppercase tracking-wide transition-colors ${qty === n ? "bg-[var(--neon-cyan)] text-black" : "border border-white/15 text-[var(--text-muted)] hover:border-[var(--neon-cyan)] hover:text-white"}`}>
                          {n} {n === 1 ? "ticket" : "tickets"}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 flex items-center gap-3">
                    <button data-testid="qty-minus" onClick={() => setQtySafe(qty - 1)} className="h-11 w-11 rounded-xl border border-white/15 text-white flex items-center justify-center hover:border-[var(--neon-pink)]"><Minus className="h-4 w-4" /></button>
                    <input data-testid="competition-ticket-quantity-input" type="number" value={qty} onChange={(e) => setQtySafe(Number(e.target.value) || 1)} className="w-20 text-center bg-[var(--surface-2)] border border-white/15 rounded-xl py-2.5 text-white font-display text-xl" />
                    <button data-testid="qty-plus" onClick={() => setQtySafe(qty + 1)} className="h-11 w-11 rounded-xl border border-white/15 text-white flex items-center justify-center hover:border-[var(--neon-pink)]"><Plus className="h-4 w-4" /></button>
                    <span className="text-xs text-[var(--text-muted)]">Max {maxPer} per user</span>
                  </div>
                </div>

                {/* Discount */}
                <div className="mt-4">
                  <input data-testid="discount-code-input" value={discount} onChange={(e) => setDiscount(e.target.value.toUpperCase())} placeholder="Discount code (optional)" className="w-full bg-[var(--surface-2)] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--neon-cyan)]" />
                </div>

                {friendDiscount && lineTotal >= (friendDiscount.min || 0) && (
                  <div data-testid="friend-discount-nudge" className="mt-3 flex items-center gap-2 rounded-xl border border-[rgba(57,255,20,.4)] bg-[rgba(57,255,20,.06)] px-4 py-2.5 text-sm text-[var(--neon-green)]">
                    <Gift className="h-4 w-4 shrink-0" /> {gbp(friendDiscount.amount)} friend discount unlocked — applied automatically at checkout.
                  </div>
                )}

                {/* Charity round-up — lift the basket up to the nearest 10p or £1 */}
                {charityRoundupEnabled && (
                  <div className="mt-4" data-testid="charity-roundup">
                    <p className="flex items-center gap-1.5 font-display uppercase tracking-wide text-[var(--neon-pink)]"><Heart className="h-4 w-4" /> Round up for charity</p>
                    <p className="mt-0.5 text-xs text-[var(--text-muted)]">100% goes to {charityName}. Thank you!</p>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {[{ v: null, l: "No thanks", sub: "" }, { v: 0.1, l: "Nearest 10p", sub: gbp(previewRoundup(0.1)) }, { v: 1, l: "Nearest £1", sub: gbp(previewRoundup(1)) }].map((o) => (
                        <button key={o.l} data-testid={`charity-option-${o.v === null ? "none" : o.v === 0.1 ? "10p" : "1"}`} onClick={() => setCharityIncrement(o.v)}
                          className={`flex flex-col items-center rounded-xl px-2 py-2.5 text-sm font-display uppercase tracking-wide transition-colors ${charityIncrement === o.v ? "bg-[rgba(255,45,149,.15)] border border-[var(--neon-pink)] text-white" : "border border-white/10 text-[var(--text-muted)] hover:border-white/30"}`}>
                          <span>{o.l}</span>
                          {o.sub && <span className="text-[11px] opacity-80">+{o.sub}</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {isAuthed && user?.wallet_balance > 0 && (
                  <label className="mt-4 flex items-center gap-2 text-sm text-[var(--text-muted)] cursor-pointer">
                    <input data-testid="use-wallet-toggle" type="checkbox" checked={useWallet} onChange={(e) => setUseWallet(e.target.checked)} className="accent-[var(--neon-green)] h-4 w-4" />
                    Use wallet balance ({gbp(user.wallet_balance)})
                  </label>
                )}

                <div className="mt-6 space-y-1.5">
                  {roundupAmount > 0 && (
                    <>
                      <div className="flex items-center justify-between text-sm text-[var(--text-muted)]">
                        <span>Tickets ({qty})</span><span>{gbp(lineTotal)}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm text-[var(--neon-pink)]">
                        <span className="flex items-center gap-1"><Heart className="h-3.5 w-3.5" /> Charity round-up</span><span data-testid="charity-amount">{gbp(roundupAmount)}</span>
                      </div>
                    </>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--text-muted)]">Total</span>
                    <NeonPrice value={gbp(grandTotal)} color="pink" className="text-3xl" testid="purchase-total" />
                  </div>
                </div>
                <NeonButton variant="pink" className="mt-4 w-full" data-testid="competition-buy-button" onClick={buy} loading={buying} disabled={t.done || salesPaused}>
                  {salesPaused ? "Sales Paused — Maintenance" : t.done ? "Competition Closed" : neuro ? "Quick Buy — Enter Now" : "Enter Now"}
                </NeonButton>
                {maintenanceMode && isAdmin && !t.done && (
                  <p className="mt-2 text-center text-[11px] text-[var(--neon-gold)]">Maintenance is ON — you can still enter as an admin (customers are paused).</p>
                )}
                {showGolden && (
                  <div data-testid="golden-ticket-cta" className="mt-4 rounded-xl border border-[rgba(255,210,74,.5)] bg-[rgba(255,210,74,.08)] p-4">
                    <p className="flex items-center gap-1.5 font-display uppercase tracking-wide text-[var(--neon-gold)]"><Ticket className="h-4 w-4" /> You have a Golden Ticket</p>
                    <p className="mt-0.5 text-xs text-[var(--text-muted)]">Use it for a FREE entry to this competition (tickets up to {gbp(eligibleGolden.max_ticket_value ?? 1)}). Answer the skill question above first.</p>
                    <NeonButton variant="outlinePink" className="mt-2 w-full" onClick={useGolden} loading={goldenBusy} data-testid="use-golden-ticket">
                      <Ticket className="h-4 w-4" /> Use Golden Ticket — Free Entry
                    </NeonButton>
                  </div>
                )}
                <p className="mt-3 text-[11px] text-[var(--text-muted)] flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> 18+. Free postal entry available. Please play responsibly.</p>
              </>
            )}
          </NeonCard>
        </div>
      </div>

      {/* UK-law skill wrong-answer modal (no charge / no tickets / retry) */}
      {skillError && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="skill-error-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setSkillError(null)} />
          <div className="relative w-full max-w-md rounded-2xl border border-[rgba(255,0,51,.5)] bg-[var(--surface-2)] p-6 shadow-[0_0_30px_rgba(255,0,51,.25)]">
            <div className="flex items-center gap-2 text-[var(--neon-pink)]">
              <AlertTriangle className="h-6 w-6" />
              <h3 className="font-display uppercase text-xl text-white">Incorrect answer</h3>
            </div>
            <p className="mt-3 text-sm text-white" data-testid="skill-error-message">{skillError}</p>
            <p className="mt-2 text-xs text-[var(--text-muted)]">No payment was taken and no tickets were issued. Pick your answer again and re-enter — you can try as many times as you like.</p>
            <div className="mt-5 flex justify-end">
              <NeonButton variant="pink" onClick={() => setSkillError(null)} data-testid="skill-error-retry">Try again</NeonButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
